/**
 * Export the workspace as an Obsidian-compatible Markdown folder: pages become
 * `.md` files with front matter, pages with children become folders (the page
 * itself as `Folder/Folder.md`), attachments go to `Bijlagen/`.
 */
import { unzipSync, zipSync, strToU8 } from 'fflate';
import { serializeMarkdown } from '@nexus/markdown';
import { buildTree, type PageNode, type Store } from '@nexus/store';
import type { VaultFile } from './analyze';
import { isEmptyDoc, loadPageJSON } from './content';

export const ATTACHMENT_FOLDER = 'Bijlagen';

/** A safe file/folder name on Windows, macOS and Linux (and in Obsidian links). */
export function safeName(title: string): string {
  const cleaned = title
    .replace(/[\\/:*?"<>|#^[\]]/g, '-')
    .replace(/\p{Cc}/gu, '') // control characters
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/[. ]+$/, '');
  const name = cleaned || 'Naamloos';
  return /^(con|prn|aux|nul|com\d|lpt\d)$/i.test(name) ? `${name}_` : name;
}

function unique(name: string, taken: Set<string>): string {
  let candidate = name;
  for (let i = 2; taken.has(candidate.toLowerCase()); i++) candidate = `${name} (${i})`;
  taken.add(candidate.toLowerCase());
  return candidate;
}

export interface ExportedFile {
  path: string;
  data: Uint8Array;
}

export async function exportWorkspace(
  store: Store,
  onProgress: (done: number, total: number) => void = () => {},
): Promise<ExportedFile[]> {
  const pages = await store.listPages();
  const aliases = await store.allAliases();
  const tree = buildTree(pages);
  const files: ExportedFile[] = [];

  // Pass 1: decide every page's file name, so links can use the exported names.
  const nameOf = new Map<string, string>();
  const plan: { node: PageNode; path: string }[] = [];
  const place = (nodes: PageNode[], folder: string, taken: Set<string>) => {
    for (const node of nodes) {
      const name = unique(safeName(node.title), taken);
      nameOf.set(node.id, name);
      if (node.children.length) {
        const dir = folder ? `${folder}/${name}` : name;
        const inner = new Set<string>([name.toLowerCase()]);
        plan.push({ node, path: `${dir}/${name}.md` });
        place(node.children, dir, inner);
      } else {
        plan.push({ node, path: folder ? `${folder}/${name}.md` : `${name}.md` });
      }
    }
  };
  place(tree, '', new Set([ATTACHMENT_FOLDER.toLowerCase()]));

  // Attachments: keep their names; rename only on clashes.
  const attachmentNames = new Map<string, string>(); // original name (lower) → exported name
  const takenAttachments = new Set<string>();
  const attachments = await store.listAttachments();
  for (const a of attachments) {
    const name = unique(a.name, takenAttachments);
    if (!attachmentNames.has(a.name.toLowerCase())) attachmentNames.set(a.name.toLowerCase(), name);
    const blob = await store.attachmentData(a.id);
    if (blob) files.push({ path: `${ATTACHMENT_FOLDER}/${name}`, data: blob.data });
  }

  // Pass 2: write pages.
  for (const [i, { node, path }] of plan.entries()) {
    const doc = await loadPageJSON(store, node.id);
    const props = await store.getProps(node.id);
    // Folders without own content or properties need no note of their own.
    if (!(node.children.length && isEmptyDoc(doc) && Object.keys(props).length === 0)) {
      const md = serializeMarkdown(doc, {
        frontmatter: Object.keys(props).length ? props : null,
        // Links written via an alias keep the alias; others follow renames.
        titleOf: (id, target) =>
          aliases.get(id)?.some((a) => a.toLowerCase() === target.toLowerCase())
            ? target
            : nameOf.get(id),
      }).replace(/!\[\[([^\]|#]+)/g, (m, target: string) => {
        const renamed = attachmentNames.get(target.toLowerCase());
        return renamed ? `![[${renamed}` : m;
      });
      files.push({ path, data: strToU8(md) });
    }
    onProgress(i + 1, plan.length);
  }
  return files;
}

export function zipFiles(files: ExportedFile[]): Uint8Array {
  return zipSync(Object.fromEntries(files.map((f) => [f.path, f.data])), { level: 6 });
}

/**
 * A zipped export (or any zipped vault) as files for the importer. When
 * everything sits in one folder (a zipped folder rather than its contents),
 * that folder is left out, as with a chosen vault folder.
 */
export function unzipVault(zip: Uint8Array): VaultFile[] {
  const entries = Object.entries(unzipSync(zip))
    .map(([path, data]) => [path.replace(/\\/g, '/'), data] as const)
    .filter(([path]) => !path.endsWith('/'));
  const first = entries[0]?.[0].split('/')[0];
  const oneFolder =
    first !== undefined &&
    entries.length > 0 &&
    entries.every(([path]) => path.includes('/') && path.split('/')[0] === first);
  return entries.map(([path, data]) => ({
    path: oneFolder ? path.slice(first.length + 1) : path,
    size: data.length,
    read: async () => data,
  }));
}
